import React, { useRef } from "react";
import { Box, Card, Grid, Typography } from "@mui/material";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { overviewChartPalette } from "styles/theme";

const CostPerformanceIndex = ({ kpiData }) => {
    const row = kpiData?.data?.[0] || {};

    const earned = row.earned ?? 0;
    const actual = row.actual ?? 0;
    const cpi = row.cpi ?? 0;
    const actualColor = cpi >= 1 ? overviewChartPalette.teal : cpi >= 0.5 ? overviewChartPalette.amber : overviewChartPalette.coral;

    const getCostVariance = (actual, earned) => {
        return Number(earned) - Number(actual);
    };

    const costVariance = getCostVariance(actual, earned);

    const costVarianceColor = costVariance >= 0 ? overviewChartPalette.teal : overviewChartPalette.coral

    // clamp CPI between 0–2 to match gauge settings
    const gaugeValue = Math.min(Math.max(cpi, 0), 1.99);

    const chartRef = useRef(null);

    const chartOptions = {
        chart: {
            type: "gauge",
            plotBackgroundColor: null,
            plotBorderWidth: 0,
            plotShadow: false,
            height: "50%",
        },
        title: { text: "" },
        pane: {
            startAngle: -90,
            endAngle: 89.9,
            background: null,
            center: ["50%", "95%"],
            size: "180%",
        },
        yAxis: {
            min: 0,
            max: 2,
            tickPixelInterval: 0,
            labels: { enabled: false },
            lineWidth: 0,
            plotBands: [
                { from: 0, to: 0.5, color: overviewChartPalette.coral, thickness: 28 },
                { from: 0.5, to: 1, color: overviewChartPalette.amber, thickness: 28 },
                { from: 1, to: 1.5, color: overviewChartPalette.teal, thickness: 28 },
                { from: 1.5, to: 2, color: overviewChartPalette.indigo, thickness: 28 }
            ]
        },
        credits: { enabled: false },
        series: [
            {
                name: "CPI",
                data: [gaugeValue],
                dataLabels: {
                    enabled: true,
                    formatter: function () {
                        return `<div>${cpi.toFixed(2)}</div>`;
                    },
                    y: -60,
                    style: {
                        fontSize: "30px",
                        fontWeight: 600,
                    },
                },
                dial: {
                    radius: "80%",
                    backgroundColor: overviewChartPalette.navy,
                    baseWidth: 7,
                    rearLength: "0%",
                },
                pivot: { backgroundColor: overviewChartPalette.navy, radius: 5 },
            },
        ],
    };

    return (
        <Card elevation={0} sx={{ borderRadius: "3px", border: "1px solid #E4E4E4", width: "100%", height: "100%" }}>
            <Box sx={{ p: 1.5, borderBottom: "1px solid #E4E4E4" }}>
                <Typography sx={{ fontWeight: 600, fontSize: "20px", fontFamily: "Outfit" }}>
                    Cost Efficiency
                </Typography>
                <Typography sx={{ fontWeight: 300, fontSize: "16px", color: "#7D7F81" }}>
                    Assess how efficiently delivered value is converting project expenditure.
                </Typography>
            </Box>

            <Grid container alignItems={"center"}>
                <Grid size={{ md: 4 }}>
                    <Box sx={{ p: 1.5 }}>
                        {/* Earned Value */}
                        <Box sx={{ mb: 1 }}>
                            <Typography sx={{ fontSize: "13px", color: "#5F6368" }}>
                                Earned Value
                            </Typography>
                            <Typography sx={{ fontSize: "24px", color: overviewChartPalette.indigo }}>
                                INR {Number(earned).toFixed(2)} Cr
                            </Typography>
                        </Box>

                        {/* Actual Cost */}
                        <Box sx={{ mb: 1 }}>
                            <Typography sx={{ fontSize: "13px", color: "#5F6368" }}>
                                Actual Cost
                            </Typography>
                            <Typography sx={{ fontSize: "24px", color: actualColor }}>
                                INR {Number(actual).toFixed(2)} Cr
                            </Typography>
                        </Box>

                        <Box sx={{ mb: 1 }}>
                            <Typography sx={{ fontSize: "13px", color: "#5F6368" }}>
                                Cost Variance
                            </Typography>
                            <Typography sx={{ fontSize: "24px", color: costVarianceColor }}>
                                INR {costVariance.toFixed(2)} Cr
                            </Typography>
                        </Box>
                    </Box>
                </Grid>

                <Grid size={{ md: 8 }}>
                    <HighchartsReact highcharts={Highcharts} options={chartOptions} ref={chartRef} />
                </Grid>
            </Grid>
        </Card>
    );
};

export default CostPerformanceIndex;
